import axios from "axios";
import imageCompression from "browser-image-compression";
import { encode } from "blurhash";
import ApiService from "../../services/api-service";

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1.2,
  maxWidthOrHeight: 1080,
  useWebWorker: true,
  fileType: "image/webp",
};

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function generateBlurhash(imgEl) {
  // Downscale to ~32px for fast blurhash encoding
  const targetW = 32;
  const targetH = Math.max(1, Math.round((imgEl.height / imgEl.width) * targetW));
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(imgEl, 0, 0, targetW, targetH);
  const { data } = ctx.getImageData(0, 0, targetW, targetH);
  return encode(data, targetW, targetH, 4, 4);
}

// Compress one file → blurhash → presign → upload to S3. Returns the image
// metadata the backend needs to register it (no DB write yet).
async function prepareImage(file) {
  const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
  const imgEl = await loadImageElement(compressed);
  const blurhash = generateBlurhash(imgEl);

  const presignRes = await ApiService.GALLERY_PRESIGN({ mimeType: compressed.type });
  const presign = presignRes && (presignRes.result || presignRes.data || presignRes);
  if (!presign || !presign.uploadUrl) {
    throw new Error("Failed to get upload URL");
  }

  await axios.put(presign.uploadUrl, compressed, {
    headers: { "Content-Type": compressed.type, "x-amz-acl": "public-read" },
  });

  return {
    key: presign.key,
    mimeType: compressed.type,
    width: imgEl.width,
    height: imgEl.height,
    sizeBytes: compressed.size,
    blurhash,
  };
}

// Upload one or more files as a SINGLE post (Instagram-style carousel): the
// images share one caption, like count, and comment thread. Returns the
// created gallery post document.
export async function uploadGalleryPost(files, { visibility = "public", caption = "" } = {}) {
  const list = Array.isArray(files) ? files : [files];
  const images = [];
  for (const file of list) {
    images.push(await prepareImage(file));
  }

  const createRes = await ApiService.GALLERY_CREATE({ images, visibility, caption });
  return createRes && (createRes.result || createRes.data || createRes);
}

// Back-compat helper: upload a single file as its own post.
export async function uploadGalleryImage(file, opts = {}) {
  return uploadGalleryPost([file], opts);
}
